import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  where,
  orderBy,
} from 'firebase/firestore';
import { addDays, addMonths, format, startOfMonth, subMonths } from 'date-fns';
import { auth, db, handleFirestoreError, OperationType } from './firebase';
import { isPlatformAdmin } from './adminAccess';
import { adminUsersService, type AdminUserListItem } from './adminUsers';
import {
  DEFAULT_PLAN_CATALOG,
  normalizePlanDefinition,
  type BuiltInPlanId,
  type PlanCatalogDoc,
  type PlanDefinition,
  type PlanId,
  type PlatformPayment,
  type PlatformSubscription,
  type RevenueAnalytics,
  type PaymentStatus,
  type SubscriptionStatus,
  planMrr,
} from './billing/types';
import { safeParseDate } from './utils';

const SUBS_COL = 'platform_subscriptions';
const PAYMENTS_COL = 'platform_payments';
const CATALOG_DOC = 'system/planCatalog';

function nowIso() {
  return new Date().toISOString();
}

function defaultPeriodEnd() {
  return addMonths(new Date(), 1).toISOString();
}

export const BUILT_IN_PLANS: BuiltInPlanId[] = ['free', 'pro', 'chamber', 'enterprise'];

/** Ensures all four built-in tiers exist and inherit any missing entitlement keys from defaults. */
export function mergeBuiltInPlansIntoCatalog(plans: PlanDefinition[]): PlanDefinition[] {
  const map = new Map<string, PlanDefinition>();
  for (const p of plans) {
    map.set(p.id, normalizePlanDefinition(p));
  }
  for (const id of BUILT_IN_PLANS) {
    const defaultPlan = DEFAULT_PLAN_CATALOG.find((p) => p.id === id)!;
    const existing = map.get(id);
    if (!existing) {
      map.set(id, defaultPlan);
    } else {
      map.set(
        id,
        normalizePlanDefinition({
          ...defaultPlan,
          ...existing,
          id,
          entitlements: { ...defaultPlan.entitlements, ...existing.entitlements },
        }),
      );
    }
  }
  const builtIn = BUILT_IN_PLANS.map((id) => map.get(id)!);
  const custom = [...map.values()].filter(
    (p) => !BUILT_IN_PLANS.includes(p.id as BuiltInPlanId),
  );
  custom.sort((a, b) => a.name.localeCompare(b.name));
  return [...builtIn, ...custom];
}

export async function getSystemBillingConfig(): Promise<{
  storageEnforcementEnabled: boolean;
}> {
  try {
    const snap = await getDoc(doc(db, 'system', 'config'));
    if (!snap.exists()) return { storageEnforcementEnabled: true };
    const data = snap.data();
    return {
      storageEnforcementEnabled: data.storageEnforcementEnabled !== false,
    };
  } catch {
    return { storageEnforcementEnabled: true };
  }
}

export async function saveSystemBillingConfig(input: {
  storageEnforcementEnabled: boolean;
}): Promise<void> {
  await setDoc(
    doc(db, 'system', 'config'),
    {
      storageEnforcementEnabled: input.storageEnforcementEnabled,
      updatedAt: nowIso(),
    },
    { merge: true },
  );
}

export function formatBillingCurrency(amount: number, currency = 'PKR') {
  if (currency === 'PKR') {
    if (amount >= 1000) return `Rs ${(amount / 1000).toFixed(1)}k`;
    return `Rs ${amount.toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
  }
  return `${currency} ${amount.toFixed(2)}`;
}

export const adminBillingService = {
  async getPlanCatalog(): Promise<PlanDefinition[]> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return DEFAULT_PLAN_CATALOG;
    }

    try {
      const canSeed = isPlatformAdmin(
        currentUser.email ?? undefined,
        undefined,
        undefined,
      );
      const snap = await getDoc(doc(db, 'system', 'planCatalog'));
      if (snap.exists()) {
        const data = snap.data() as PlanCatalogDoc;
        if (data.plans?.length) {
          const merged = mergeBuiltInPlansIntoCatalog(
            data.plans.map((p) => normalizePlanDefinition({ ...p, id: p.id })),
          );
          const hadMissingBuiltIn = BUILT_IN_PLANS.some(
            (id) => !data.plans!.some((p) => p.id === id),
          );
          if (hadMissingBuiltIn && canSeed) {
            await setDoc(
              doc(db, 'system', 'planCatalog'),
              { plans: merged, updatedAt: nowIso() },
              { merge: true },
            );
          }
          return merged;
        }
      }

      if (canSeed) {
        await setDoc(
          doc(db, 'system', 'planCatalog'),
          { plans: DEFAULT_PLAN_CATALOG, updatedAt: nowIso() },
          { merge: true },
        );
      }
      return DEFAULT_PLAN_CATALOG;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      if (
        msg.includes('permission-denied') ||
        msg.includes('Missing or insufficient permissions')
      ) {
        return DEFAULT_PLAN_CATALOG;
      }
      handleFirestoreError(error, OperationType.GET, 'system/planCatalog');
      return DEFAULT_PLAN_CATALOG;
    }
  },

  async savePlanCatalog(plans: PlanDefinition[]): Promise<void> {
    await setDoc(
      doc(db, 'system', 'planCatalog'),
      { plans, updatedAt: nowIso() } satisfies PlanCatalogDoc,
      { merge: true },
    );
  },

  async listSubscriptions(): Promise<PlatformSubscription[]> {
    try {
      const snap = await getDocs(
        query(collection(db, SUBS_COL), orderBy('updatedAt', 'desc'), limit(500)),
      );
      const subs = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as PlatformSubscription);

      if (subs.length > 0) return subs;
      return adminBillingService.syncSubscriptionsFromUsers();
    } catch (error) {
      console.warn('[adminBilling] listSubscriptions', error);
      return adminBillingService.syncSubscriptionsFromUsers();
    }
  },

  async syncSubscriptionsFromUsers(): Promise<PlatformSubscription[]> {
    const [users, catalog] = await Promise.all([
      adminUsersService.listUsers(),
      adminBillingService.getPlanCatalog(),
    ]);
    const synced: PlatformSubscription[] = [];

    for (const u of users) {
      const sub = userToSubscription(u, catalog);
      synced.push(sub);
      try {
        await setDoc(doc(db, SUBS_COL, u.id), sub, { merge: true });
      } catch (e) {
        console.warn('[adminBilling] sync sub', u.id, e);
      }
    }

    return synced.sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  },

  async getSubscription(userId: string): Promise<PlatformSubscription | null> {
    try {
      const snap = await getDoc(doc(db, SUBS_COL, userId));
      if (snap.exists()) return { id: snap.id, ...snap.data() } as PlatformSubscription;
    } catch {
      /* fall through */
    }
    const users = await adminUsersService.listUsers();
    const user = users.find((u) => u.id === userId);
    if (!user) return null;
    const catalog = await adminBillingService.getPlanCatalog();
    return userToSubscription(user, catalog);
  },

  async listPayments(): Promise<PlatformPayment[]> {
    try {
      const snap = await getDocs(
        query(collection(db, PAYMENTS_COL), orderBy('createdAt', 'desc'), limit(500)),
      );
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as PlatformPayment);
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, PAYMENTS_COL);
      return [];
    }
  },

  async listPaymentsByStatus(status: PaymentStatus): Promise<PlatformPayment[]> {
    try {
      const snap = await getDocs(
        query(
          collection(db, PAYMENTS_COL),
          where('status', '==', status),
          orderBy('createdAt', 'desc'),
          limit(200),
        ),
      );
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as PlatformPayment);
    } catch {
      const all = await adminBillingService.listPayments();
      return all.filter((p) => p.status === status);
    }
  },

  async getRevenueAnalytics(): Promise<RevenueAnalytics> {
    const [subs, payments, catalog, users] = await Promise.all([
      adminBillingService.listSubscriptions(),
      adminBillingService.listPayments(),
      adminBillingService.getPlanCatalog(),
      adminUsersService.listUsers(),
    ]);

    const activeSubs = subs.filter((s) => s.status === 'active' || s.status === 'trialing');
    const mrr = activeSubs.reduce((sum, s) => sum + (s.mrr || 0), 0);
    const paidUsers = activeSubs.filter((s) => s.plan !== 'free').length;
    const totalUsers = users.length || 1;

    const canceledLast30 = subs.filter((s) => {
      if (s.status !== 'canceled' || !s.canceledAt) return false;
      const d = safeParseDate(s.canceledAt);
      return d && d > subMonths(new Date(), 1);
    }).length;
    const activeStart = activeSubs.length + canceledLast30;
    const churnRate =
      activeStart > 0 ? Math.round((canceledLast30 / activeStart) * 1000) / 10 : 0;
    const retentionRate = Math.max(0, Math.round((100 - churnRate) * 10) / 10);

    const succeeded = payments.filter((p) => p.status === 'succeeded');
    const failedPaymentsCount = payments.filter((p) => p.status === 'failed').length;
    const revenuePerUser =
      paidUsers > 0 ? Math.round(mrr / paidUsers) : 0;

    const revenueByPlan: RevenueAnalytics['revenueByPlan'] = BUILT_IN_PLANS.map((plan) => {
      const planSubs = activeSubs.filter((s) => s.plan === plan);
      return {
        plan,
        count: planSubs.length,
        mrr: planSubs.reduce((s, x) => s + x.mrr, 0),
      };
    });

    const monthKeys = Array.from({ length: 6 }, (_, i) => {
      const d = startOfMonth(subMonths(new Date(), 5 - i));
      return { month: format(d, 'yyyy-MM'), label: format(d, 'MMM yyyy'), date: d };
    });

    const revenueHistory = monthKeys.map(({ month, label }) => {
      const value = succeeded
        .filter((p) => {
          const d = safeParseDate(p.createdAt);
          return d && format(startOfMonth(d), 'yyyy-MM') === month;
        })
        .reduce((s, p) => s + p.amount, 0);
      return { month, label, value };
    });

    const churnHistory = monthKeys.map(({ month, label }) => {
      const value = subs.filter((s) => {
        if (!s.canceledAt) return false;
        const d = safeParseDate(s.canceledAt);
        return d && format(startOfMonth(d), 'yyyy-MM') === month;
      }).length;
      return { month, label, value };
    });

    return {
      mrr,
      arr: mrr * 12,
      activeSubscriptions: activeSubs.length,
      paidUsers,
      churnRate,
      retentionRate,
      revenuePerUser,
      failedPaymentsCount,
      revenueByPlan,
      revenueHistory,
      churnHistory,
    };
  },
};

function userToSubscription(
  u: AdminUserListItem,
  catalog: PlanDefinition[],
): PlatformSubscription {
  const sub = u.subscription as Record<string, unknown> | undefined;
  const plan = String(u.planTier ?? sub?.plan ?? 'free').toLowerCase();
  const validPlan = plan;
  const status = String(sub?.status ?? (validPlan === 'free' ? 'active' : 'active')) as SubscriptionStatus;
  const currency = catalog.find((p) => p.id === validPlan)?.currency ?? 'PKR';
  const periodEnd =
    (sub?.currentPeriodEnd as string) ??
    (sub?.grantedAt ? addMonths(safeParseDate(String(sub.grantedAt)) ?? new Date(), 1).toISOString() : defaultPeriodEnd());

  return {
    id: u.id,
    userId: u.id,
    userEmail: u.email ?? '',
    userName: u.displayName ?? u.name,
    plan: validPlan as PlanId,
    status: ['active', 'paused', 'canceled', 'past_due', 'trialing'].includes(status)
      ? status
      : 'active',
    mrr: planMrr(validPlan, catalog),
    currency,
    currentPeriodStart: (sub?.currentPeriodStart as string) ?? u.createdAt ?? nowIso(),
    currentPeriodEnd: periodEnd,
    createdAt: u.createdAt ?? nowIso(),
    updatedAt: nowIso(),
    source: (sub?.source as PlatformSubscription['source']) ?? 'user_sync',
    pausedAt: sub?.pausedAt as string | undefined,
    canceledAt: sub?.canceledAt as string | undefined,
  };
}

export function buildSubscriptionDoc(
  input: Partial<PlatformSubscription> & {
    userId: string;
    userEmail: string;
    plan: PlanId;
    catalog: PlanDefinition[];
  },
): PlatformSubscription {
  const mrr = planMrr(input.plan, input.catalog);
  const currency =
    input.currency ?? input.catalog.find((p) => p.id === input.plan)?.currency ?? 'PKR';
  return {
    id: input.userId,
    userId: input.userId,
    userEmail: input.userEmail,
    userName: input.userName,
    plan: input.plan,
    status: input.status ?? 'active',
    mrr,
    currency,
    currentPeriodStart: input.currentPeriodStart ?? nowIso(),
    currentPeriodEnd: input.currentPeriodEnd ?? defaultPeriodEnd(),
    createdAt: input.createdAt ?? nowIso(),
    updatedAt: nowIso(),
    source: input.source ?? 'admin',
    pausedAt: input.pausedAt,
    canceledAt: input.canceledAt,
  };
}

export function extendPeriodEnd(currentEnd: string, days: number): string {
  const base = safeParseDate(currentEnd) ?? new Date();
  return addDays(base, days).toISOString();
}
